import React, { useEffect, useMemo } from 'react';
import {
  servicesConstants, useFeatureFlagsContext, useI18n, useRootContext, useServicesQuery,
} from '@jarvis/react-portal-addons';
import GenericThemeProvider from '../../../shared/GenericThemeProvider';
import {
  Container, Grid, PrintAnywhereContainer, SideCardsContainer,
} from './styles';
import useDeviceCacheApiCall from '../../../hooks/useDeviceCacheApiCall';
import Banner from '../Banner';
import { AdminsWidget } from '../AdminsWidget';
import { WorkspaceWidget } from '../WorkspaceWidget';
import PrintAnywhereWidget from '../PrintAnywhereWidget';
import { EndUserViewHomeScreenDisplayed, publishEvent } from '../../../utils/analytics';

const { SERVICE_TYPES, SERVICE_STATES, SERVICE_LEVELS } = servicesConstants;

const EndUserConsolidatedDashboard = () => {
  const { shell, stack } = useRootContext();
  const { t } = useI18n();

  const { authProvider } = shell;

  const deviceCache = useDeviceCacheApiCall({
    authProvider,
    stack,
    init: true,
  });

  const { consolidatedPortalCheckAdvancedViewGrant, grantsSupport } = useFeatureFlagsContext();

  const advancedViewQuery = useMemo(
    () => ({
      type: SERVICE_TYPES.GRANTS,
      services: [
        {
          id: 'advancedView',
          states: [SERVICE_STATES.ENABLED],
          serviceIds: ['solution.client.portal/advanced-view'],
        },
      ],
    }), [],
  );

  const { helpers: servicesHelpers } = useServicesQuery(advancedViewQuery);

  const hasAdvancedGrant = servicesHelpers?.checkService({ id: 'advancedView' });

  const deviceIds = useMemo(
    () => deviceCache?.data?.map(({ deviceId }) => deviceId) ?? [],
    [deviceCache?.data],
  );

  const printAnywhereQuery = useMemo(
    () => ({
      type: grantsSupport ? SERVICE_TYPES.GRANTS : SERVICE_TYPES.ENTITLEMENTS,
      level: SERVICE_LEVELS.DEVICE,
      deviceIds,
      services: [
        {
          id: 'printAnywhereEnabled',
          states: [SERVICE_STATES.ENABLED, SERVICE_STATES.PENDING],
          serviceIds: ['ws-hp.com/potg'],
        },
      ],
    }), [grantsSupport, deviceIds],
  );

  const { helpers: printAnywhereServicesHelpers } = useServicesQuery(printAnywhereQuery);

  const hasPrintAnywhereEntitlement = printAnywhereServicesHelpers
    ?.countDeviceServices?.({ id: 'printAnywhereEnabled' }) > 0;

  useEffect(() => {
    publishEvent(EndUserViewHomeScreenDisplayed);
  }, []);

  return (
    <GenericThemeProvider>
      <Container data-testid="end-user-dashboard">
        <Banner
          data-testid="banner-container" deviceCache={deviceCache}
          showNotifications={false}
        />

        <Grid>
          {hasPrintAnywhereEntitlement && (
            <PrintAnywhereContainer data-testid="print-anywhere-container">
              <PrintAnywhereWidget stack={stack} />
            </PrintAnywhereContainer>
          )}

          <SideCardsContainer data-testid="side-cards-container" fullWidth={!hasPrintAnywhereEntitlement}>
            {consolidatedPortalCheckAdvancedViewGrant && !hasAdvancedGrant ? (
              <AdminsWidget title={t('dashboard.printerOwnersListCard.header')} />
            ) : (
              <>
                <AdminsWidget title={t('dashboard.adminsListCard.header')} />
                <WorkspaceWidget />
              </>
            )}
          </SideCardsContainer>

        </Grid>
      </Container>
    </GenericThemeProvider>
  );
};

export default EndUserConsolidatedDashboard;
